import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { Feature, Point } from 'geojson';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queries, type ComuneLight } from '@/data';
import type { PoiCategory } from './types';
import { POI_CONFIG } from './poi-config';
import { usePoiData } from './usePoiData';
import { uiLang } from '@/i18n/lang';

interface PoiLayerProps {
  category: PoiCategory;
}

// POI rendering strategy (v3 — 2026-05-24, requested by user):
//   zoom ≤ 7      → admin clusters by province (country-wide overview).
//                   Even a phone can paint ~110 buckets fast, and individual
//                   dots are visually noisy at this scale.
//   zoom 8 – 13   → canvas dot per POI (no tooltip, no label, no popup-on-hover).
//                   Pure "where is the density" view. ~2-4 px radius, grows
//                   with zoom. Stays well above the choropleth mask via the
//                   dedicated `poiPane` (zIndex 620, see BaseMap).
//   zoom ≥ 14     → full divIcon marker + sticky tooltip + click popup.
//                   Tooltip costs (DOM node + listeners per marker) are
//                   amortised because viewport bbox at this zoom holds at
//                   most a couple of hundred POI.
//
// Why this split: prior versions clustered up to zoom 12, so the user only
// saw aggregate badges instead of actual POI distribution while exploring
// regions — which doesn't represent reality (e.g. a province with 200 schools
// vs one with 8 looked identical until you clicked through).
const PROVINCE_CLUSTER_MAX_ZOOM = 7;
const DOTS_MIN_ZOOM = 8;
const DETAILED_MARKERS_MIN_ZOOM = 14;

type AdminKey = string;
interface AdminBucket {
  key: AdminKey;
  level: 'province' | 'comune';
  features: Array<Feature<Point>>;
}

// Ключ родительской единицы в POI-файлах называется по-своему в каждой стране:
// у Италии это буквенный акроним prov_acr, у Испании — двузначный код cpro,
// у Португалии — четырёхзначный dtmn (concelho: самый крупный код, который
// португальские POI вообще несут). Все ведут себя одинаково, поэтому берём
// первый попавшийся непустой из известных имён. Имена не пересекаются между
// странами, так что порядок ни на кого не влияет.
// Новая страна добавляет сюда своё имя — и только сюда.
const PROVINCE_KEY_PROPS = ['prov_acr', 'cpro', 'dtmn'] as const;

function provinceKeyFor(props: Record<string, unknown>): { level: 'province'; key: string } | null {
  for (const name of PROVINCE_KEY_PROPS) {
    const k = props[name];
    if (typeof k === 'string' && k.length > 0) return { level: 'province', key: k };
  }
  return null;
}

// Coarse grid fallback for POI whose file carries no province key at all.
// ~0.5° ≈ 40 km — matches province-level density.
function gridKeyFor(lat: number, lon: number): { level: 'province'; key: string } {
  return { level: 'province', key: `grid:${Math.floor(lat * 2)}:${Math.floor(lon * 2)}` };
}

// Dot radius grows with zoom so the dots stay readable as the map zooms in
// (matching how a user expects "object density" to read). Kept small enough
// not to overlap on dense areas like Milan.
function dotRadiusForZoom(zoom: number): number {
  if (zoom <= 8) return 2.5;
  if (zoom <= 10) return 3;
  if (zoom <= 12) return 3.5;
  return 4;
}

function makeAdminClusterIcon(color: string, count: number, label: string): L.DivIcon {
  // Размер растёт с количеством, но мягче чем у L.markerClusterGroup —
  // нам важнее показать админ-имя, а не загромождать карту большими бабблами.
  const size = count < 10 ? 30 : count < 100 ? 36 : count < 1000 ? 42 : 48;
  const fontSize = size < 36 ? 11 : 12;
  const countLabel = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : `${count}`;
  return L.divIcon({
    html: `
      <div style="
        display:flex;
        flex-direction:column;
        align-items:center;
        gap:2px;
      ">
        <div style="
          width:${size}px;
          height:${size}px;
          background:${color};
          color:#fff;
          font-weight:700;
          font-size:${fontSize}px;
          display:flex;
          align-items:center;
          justify-content:center;
          border-radius:9999px;
          border:2px solid rgba(255,255,255,0.9);
          box-shadow:0 1px 4px rgba(0,0,0,0.35);
        ">${countLabel}</div>
        <div style="
          background:rgba(255,255,255,0.92);
          color:#1c1a16;
          font-size:10px;
          font-weight:600;
          padding:1px 6px;
          border-radius:4px;
          white-space:nowrap;
          box-shadow:0 1px 2px rgba(0,0,0,0.25);
          max-width:120px;
          overflow:hidden;
          text-overflow:ellipsis;
        ">${label}</div>
      </div>
    `,
    className: '',
    iconSize: [size + 40, size + 18],
    iconAnchor: [(size + 40) / 2, size / 2],
  });
}

function makePinIcon(color: string, emoji: string): L.DivIcon {
  return L.divIcon({
    html: `
      <div style="
        width:24px;
        height:24px;
        background:${color};
        border:2px solid #fff;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:13px;
        line-height:1;
        box-shadow:0 1px 3px rgba(0,0,0,0.4);
      ">${emoji}</div>
    `,
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

function centroidOf(features: Array<Feature<Point>>): [number, number] {
  let latSum = 0;
  let lonSum = 0;
  for (const f of features) {
    const [lon, lat] = f.geometry.coordinates;
    latSum += lat;
    lonSum += lon;
  }
  return [latSum / features.length, lonSum / features.length];
}

export function PoiLayer({ category }: PoiLayerProps) {
  const map = useMap();
  const { t, i18n } = useTranslation();
  const cfg = POI_CONFIG[category];
  const { data } = usePoiData(category);

  const { data: meta } = useQuery(queries.meta());
  const { data: lightIndex } = useQuery({
    ...queries.lightIndex(meta?.version ?? ''),
    enabled: !!meta?.version,
  });

  const lang: 'en' | 'ru' = uiLang(i18n.language);
  const categoryLabel = t(`poi.category.${category}`, { defaultValue: cfg.label });

  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!data) return;

    const pinIcon = makePinIcon(cfg.color, cfg.emoji);
    // Shared canvas renderer for the lightweight dot markers — avoids one SVG
    // path per marker (which is what causes the freeze when several thousand
    // hospitals/schools are visible at once). Bound to the dedicated `poiPane`
    // (zIndex 620) so the dots paint ABOVE the choropleth mask (overlayPane=400).
    const dotCanvas = L.canvas({ padding: 0.2, pane: 'poiPane' });
    const group = L.layerGroup().addTo(map);
    layerRef.current = group;

    let rafId: number | null = null;
    const scheduleRender = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        render();
      });
    };

    const render = () => {
      group.clearLayers();
      const zoom = map.getZoom();
      const features = (data.features as unknown as Array<Feature<Point>>) || [];

      // Wide-zoom country view → province clusters. Anything zoomed-in enough
      // to be exploring a region (zoom 8+) shows every POI as a dot.
      if (zoom <= PROVINCE_CLUSTER_MAX_ZOOM) {
        const buckets = new Map<string, AdminBucket>();
        for (const f of features) {
          const props = (f.properties ?? {}) as Record<string, unknown>;
          const admin = provinceKeyFor(props);
          const resolved = admin ?? (() => {
            const [fLon, fLat] = f.geometry.coordinates;
            return gridKeyFor(fLat, fLon);
          })();
          const bucketKey = `${resolved.level}:${resolved.key}`;
          const b = buckets.get(bucketKey);
          if (b) b.features.push(f);
          else buckets.set(bucketKey, { key: resolved.key, level: resolved.level, features: [f] });
        }
        buckets.forEach((b) => {
          if (b.features.length === 1) {
            renderDot(b.features[0]);
            return;
          }
          const [lat, lon] = centroidOf(b.features);
          const label = adminLabel(b, lightIndex);
          const icon = makeAdminClusterIcon(cfg.color, b.features.length, label);
          const m = L.marker([lat, lon], { icon, zIndexOffset: 100, pane: 'poiPane' });
          m.bindPopup(adminClusterPopup(b, cfg, lightIndex), { maxWidth: 320 });
          m.on('click', () => map.flyTo([lat, lon], DOTS_MIN_ZOOM, { duration: 0.6 }));
          group.addLayer(m);
        });
        return;
      }

      // Mid-to-far zoom (8-13): pure dots. Viewport culling kicks in only past
      // zoom 11 — until then the dataset is small enough that culling+pan
      // would cost more than just drawing all dots once and reusing them.
      const useCulling = zoom >= 11;
      const bounds = useCulling ? map.getBounds().pad(0.25) : null;

      const detailed = zoom >= DETAILED_MARKERS_MIN_ZOOM;
      const dotRadius = dotRadiusForZoom(zoom);

      for (const f of features) {
        const [fLon, fLat] = f.geometry.coordinates;
        if (bounds && !bounds.contains([fLat, fLon])) continue;
        if (detailed) {
          renderDetailed(f, fLat, fLon);
        } else {
          renderDot(f, fLat, fLon, dotRadius);
        }
      }

      function renderDot(
        f: Feature<Point>,
        latArg?: number,
        lonArg?: number,
        radius: number = dotRadiusForZoom(zoom),
      ) {
        const [fLon, fLat] = latArg != null && lonArg != null ? [lonArg, latArg] : f.geometry.coordinates;
        // No tooltip, no label, no popup — just a dot. The user explicitly
        // asked for the mid-zoom view to be "just position" with all detail
        // gated to the close-up zoom.
        const dot = L.circleMarker([fLat, fLon], {
          renderer: dotCanvas,
          pane: 'poiPane',
          radius,
          color: '#ffffff',
          weight: 1,
          fillColor: cfg.color,
          fillOpacity: 0.95,
          interactive: false,
        });
        group.addLayer(dot);
      }

      function renderDetailed(f: Feature<Point>, lat: number, lon: number) {
        const props = f.properties ?? {};
        const marker = L.marker([lat, lon], { icon: pinIcon, pane: 'poiPane' });
        marker.bindTooltip(cfg.tooltipFormatter(props), {
          sticky: true,
          direction: 'top',
          offset: [0, -12],
        });
        if (cfg.popupFormatter) {
          marker.bindPopup(
            cfg.popupFormatter(props, { lat, lon, category, lang, categoryLabel }),
            {
              maxWidth: 280,
              closeButton: true,
              autoPan: true,
            },
          );
        }
        group.addLayer(marker);
      }
    };

    render();
    map.on('zoomend', scheduleRender);
    map.on('moveend', scheduleRender);

    return () => {
      map.off('zoomend', scheduleRender);
      map.off('moveend', scheduleRender);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      map.removeLayer(group);
      layerRef.current = null;
    };
  }, [map, data, cfg, category, lightIndex, categoryLabel, lang]);

  return null;
}

function adminLabel(bucket: AdminBucket, lightIndex: { byIstat: Map<string, ComuneLight> } | undefined): string {
  if (bucket.key.startsWith('grid:')) return '';
  if (bucket.level === 'comune' && lightIndex) {
    const c = lightIndex.byIstat.get(bucket.key);
    if (c) return c.name;
  }
  return bucket.key;
}

function adminClusterPopup(
  bucket: AdminBucket,
  cfg: { label: string },
  lightIndex: { byIstat: Map<string, ComuneLight> } | undefined,
): string {
  const label = adminLabel(bucket, lightIndex);
  const level = bucket.level === 'province' ? 'провинция' : 'comune';
  const sampleNames = bucket.features
    .slice(0, 6)
    .map((f) => {
      const n = f.properties?.name;
      return typeof n === 'string' ? n : '—';
    });
  const more = bucket.features.length - sampleNames.length;
  const list = sampleNames
    .map((n) => `<li style="margin:0;padding:1px 0;">${escapeHtml(n)}</li>`)
    .join('');
  return `
    <div style="font-family:inherit;">
      <div style="font-weight:600;font-size:13px;color:#1c1a16;">${escapeHtml(label)}</div>
      <div style="font-size:11px;color:#6b6557;margin-bottom:6px;">${escapeHtml(level)} · ${bucket.features.length} ${escapeHtml(cfg.label.toLowerCase())}</div>
      <ul style="margin:0;padding:0;list-style:none;font-size:11px;color:#3a3730;">${list}</ul>
      ${more > 0 ? `<div style="font-size:10px;color:#8c8676;margin-top:4px;">+ ещё ${more}</div>` : ''}
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return ch;
    }
  });
}
